import { useCallback, useMemo, useState } from 'react'
import { getPartitionKeysFromAllNestedSubElements, useSlsStateContext, useSlsDispatchContext } from '@fs/zion-sls'

/**
 * @typedef {Object} MultiImageArksWarningResult
 * @property {boolean} showWarning - Whether to show the warning about missing images.
 * @property {boolean} disableEdit - Whether editing should be disabled due to missing images or loading state.
 * @property {string[]} neededImageArks - List of missing partition keys (ARKs) that need to be loaded.
 * @property {boolean} loading - Whether the missing images are currently being loaded.
 * @property {() => void} loadMissingImages - Function to dispatch loading of missing images.
 * @property {() => void} dismissWarning - Function to dismiss the warning.
 */

/**
 * Custom React hook to manage the warning and enabling/disabling of editing for persons with multiple images associated with them.
 * It's designed to check if all necessary images (identified by partition keys) are loaded, if not, it provides a way to load them
 * and manage the UI state accordingly.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.person - The person element.
 * @param {boolean} params.disabled - Whether the hook's logic should be disabled.
 * @returns {MultiImageArksWarningResult} An object containing the state and functions to manage the multi-image warning.
 */
const useMultiImageArksWarning = ({ person, disabled = false }) => {
  const { images } = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const [hasDispatchedMissingImages, setHasDispatchedMissingImages] = useState(false)
  const [hasDismissedTheWarning, setHasDismissedTheWarning] = useState(false)

  // Get the partition keys from the person element
  const personPartitionKeys = useMemo(
    () => (disabled ? [] : getPartitionKeysFromAllNestedSubElements({ element: person }) || []),
    [person, disabled]
  )

  // Get the already loaded image partition keys
  const alreadyLoadedImageArks = useMemo(
    () => (disabled ? [] : images?.map((img) => img?.partitionKey) || []),
    [images, disabled]
  )

  // Determine which partition keys are missing that need to be loaded
  const missingPartitionKeys = useMemo(
    () => personPartitionKeys.filter((ark) => !alreadyLoadedImageArks.includes(ark)),
    [personPartitionKeys, alreadyLoadedImageArks]
  )

  const loading = useMemo(
    () => hasDispatchedMissingImages && !missingPartitionKeys.every((ark) => alreadyLoadedImageArks.includes(ark)),
    [alreadyLoadedImageArks, missingPartitionKeys, hasDispatchedMissingImages]
  )

  const disableEdit = useMemo(() => loading || missingPartitionKeys.length > 0, [loading, missingPartitionKeys])

  // Function to load missing images. This will dispatch an action to load the missing images
  const loadMissingImages = useCallback(() => {
    setHasDispatchedMissingImages(true)

    zionSlsDispatch({
      type: 'ADD_ARKS_TO_LOAD',
      value: missingPartitionKeys,
    })
  }, [missingPartitionKeys, zionSlsDispatch])

  const dismissWarning = useCallback(() => {
    setHasDismissedTheWarning(true)
  }, [])

  return {
    showWarning: disableEdit && !hasDismissedTheWarning,
    disableEdit,
    neededImageArks: missingPartitionKeys,
    loading,
    loadMissingImages,
    dismissWarning,
  }
}

export default useMultiImageArksWarning
